import "./AddVariation.scss";
import { useEffect, useRef, useState } from "react";
import { AdminService } from "../utils";
import { useAsync } from "../../../utils/hooks/useAsync";
import { toast, ToastContainer } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import UploadImage from "../UploadImage";
import Label from "../../Label";
import useDevice from "../../../utils/hooks/useDevice";
import { getGaugeSelectOptions } from "./util";
import { displayMoney } from "../../../utils/displayMoney";
import { normalizeWastage } from "../../../utils/wastage";
import WorkmanshipSelect from "../WorkmanshipSelect";
import { ALL_COLORS, getVariantColors, isDuplicateVariant, sortVariantRows } from "../variantOptions";

type Props = {
  product: Product;
  manufacturingCost: ManufacturingCost[];
  setState: React.Dispatch<React.SetStateAction<any>>;
  existingVariations?: Variation[];
};

type WastageOption = Wastage | "";
type VariationForm = Omit<Variation, "wastage"> & { wastage: WastageOption };
type DraftVariant = VariationForm & { localId: number };

const AddVariation: React.FC<Props> = ({
  product,
  setState,
  manufacturingCost,
  existingVariations,
}) => {
  const navigate = useNavigate();
  const route = useLocation();
  const { run } = useAsync();
  const { isMobile } = useDevice();
  const [hilos, setHilos] = useState<string[]>([]);
  const [isSolid, setIsSolid] = useState<boolean>(false);
  const [longitudes, setLongitudes] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const saving = useRef(false);
  const mounted = useRef(true);
  const saveTimer = useRef<ReturnType<typeof setTimeout>>();
  const previousMaterial = useRef(product.material);
  const nextLocalId = useRef(0);
  const defaultWastage = (material: string): WastageOption => {
    if (material === "silver") {
      return "NO_WASTAGE";
    }
    return "";
  };

  const wastageLabel = (value?: WastageOption) => {
    if (value === "NO_WASTAGE") {
      return "Sin merma";
    }
    return value || "";
  };

  const [variation, setVariation] = useState<VariationForm>({
    weight: "",
    length: "",
    color: product.material == "gold" ? "" : "plata",
    wastage: defaultWastage(product.material),
    gauge: "",
    productId: product._id || "",
    manufacturingCostId: "",
  });
  const [variations, setVariations] = useState<DraftVariant[]>([]);
  // Consecutive submissions must check the rows from the latest addition,
  // including submissions received before React paints the next render.
  const currentVariations = useRef<DraftVariant[]>([]);
  const colorOptions = getVariantColors(product.material);
  const sortedVariations = sortVariantRows(variations, product.material);
  const updateVariations = (rows: DraftVariant[]) => {
    currentVariations.current = rows;
    setVariations(rows);
  };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearTimeout(saveTimer.current);
    };
  }, []);
  useEffect(() => {
    if (previousMaterial.current !== product.material) {
      previousMaterial.current = product.material;
      setVariation((draft) => ({
        ...draft,
        color: draft.color === ALL_COLORS ? ALL_COLORS : product.material === "silver" ? "plata" : "",
        manufacturingCostId: "",
        wastage: defaultWastage(product.material),
      }));
    }
  }, [product.material]);

  useEffect(() => {
    let arrayLongitud = [
      18, 21, 24, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100,
    ];

    setLongitudes(arrayLongitud);
    setHilos(getGaugeSelectOptions());

    setIsSolid(Boolean((product.department == "chain" || product.department == "brazalete") &&
      !product.category.includes("pendant") &&
      !product.category.includes("rock") &&
      !product.category.includes("hollow") &&
      !product.category.includes("hoop") &&
      !product.category.includes("finding") &&
      !product.isCustom));
  }, [product.department, product.category, product.isCustom]);

  const handleChange = (e: any) => {
    const nextValue =
      e.currentTarget.name === "wastage"
        ? (e.currentTarget.value as WastageOption)
        : e.currentTarget.value;
    setVariation({
      ...variation,
      [e.currentTarget.name]:
        e.currentTarget.name == "gauge" &&
        (product.department == "chain" || product.department == "brazalete") &&
        isSolid
          ? nextValue
          : e.currentTarget.name == "weight"
          ? String(nextValue).replace(",", ".")
          : nextValue,
    });
  };
  
  const submitForm = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    if (!sharedInputsValid || saving.current) return;
    const candidates = variation.color === ALL_COLORS
      ? colorOptions
      : colorOptions.filter((option) => option.value === variation.color);
    const rows = [...currentVariations.current];
    const skipped: string[] = [];
    let added = 0;
    candidates.forEach((option) => {
      const candidate = { ...variation, color: option.value };
      if ([...(existingVariations || []), ...rows].some((row) => isDuplicateVariant(row, candidate))) {
        skipped.push(option.label);
      } else {
        // Never carry an API identity into a new row.
        const { _id, ...values } = candidate;
        rows.push({ ...values, localId: nextLocalId.current++ });
        added++;
      }
    });
    if (added) updateVariations(rows);
    if (variation.color === ALL_COLORS) {
      const summary = added
        ? (added === 1 ? "Se agregó 1 variante." : `Se agregaron ${added} variantes.`)
        : "Las variantes ya estaban cargadas. No se agregaron variantes.";
      const duplicates = skipped.length
        ? ` Se ${skipped.length === 1 ? "omitió 1 variante repetida" : `omitieron ${skipped.length} variantes repetidas`}: ${skipped.join(", ")}.`
        : "";
      toast.info(summary + duplicates);
    }
  };

  const sharedInputsValid =
    variation.color != "" &&
    variation.gauge != "" &&
    variation.length != "" &&
    variation.manufacturingCostId != "" &&
    variation.productId != "" &&
    variation.weight.trim() !== "" &&
    Number.isFinite(Number(variation.weight)) &&
    variation.wastage != "" &&
    manufacturingCost.some((cost) => cost._id === variation.manufacturingCostId && cost.material === product.material) &&
    (variation.color === ALL_COLORS ? colorOptions.length > 0 : colorOptions.some((option) => option.value === variation.color));
  const checkedVariation = sharedInputsValid && !isSaving && (variation.color === ALL_COLORS ||
    ![...(existingVariations || []), ...variations].some((row) => isDuplicateVariant(row, variation)));

  const removeVariation = (variationref: DraftVariant) => {
    if (saving.current) return;
    toast.done("Variacion descartada");
    updateVariations(currentVariations.current.filter((row) => row.localId !== variationref.localId));
  };

  const saveVariations = async () => {
    if (saving.current || !currentVariations.current.length) return;
    saving.current = true;
    setIsSaving(true);
    const normalizedVariations: Variation[] = currentVariations.current.map(({ localId, ...vari }) => ({
      ...vari,
      wastage: normalizeWastage(vari.wastage, product.material),
    }));
    run(AdminService.saveVariant(normalizedVariations).then(() => {
      if (!mounted.current) return;
      toast.success("Variaciones guardadas correctamente");
      saveTimer.current = setTimeout(() => {
        setState(true);
        updateVariations([]);
        navigate("/admin/see");
      }, 2000);
    }).catch(() => {
      if (!mounted.current) return;
      saving.current = false;
      setIsSaving(false);
      toast.error("No se pudieron guardar las variantes. Los cambios siguen cargados para reintentar.");
    }));
  };

  const manufacturingCostVariation = (vari: VariationForm) =>
    manufacturingCost.find((el) => el._id == vari.manufacturingCostId)?.value;

  return (
    <div className="addVariationContainer">
      <ToastContainer autoClose={1500} position={"bottom-left"} />

      {!route.pathname.includes("admin/edit") && (
        <>
          <Label
            tag="h3"
            style={{
              textAlign: "left",
              fontFamily: "Mont-Semibold",
            }}
          >
            {product?.title}
          </Label>
          <UploadImage product={product} urlImage={product.thumbnailUrl} />
        </>
      )}
      <Label
        tag="h3"
        style={{
          textAlign: "left",
          fontFamily: "Mont-Semibold",
          fontSize: isMobile ? "16px" : "18px",
        }}
      >
        Variantes
      </Label>
      <form onSubmit={submitForm} className="add_variation_form_container">
        <div className="add_variation_form">
          {(product.department == "chain" ||
            product.department == "brazalete") &&
          isSolid ? (
            <>
              <label>
                Hilo:
                <select
                  name="gauge"
                  onChange={handleChange}
                  onClick={handleChange}
                  value={variation.gauge}
                >
                  <option value="" selected disabled hidden>
                    Seleccione
                  </option>
                  {hilos &&
                    hilos.map((hilo) => {
                      return (
                        <option key={hilo} value={hilo}>
                          {hilo}
                        </option>
                      );
                    })}
                </select>
              </label>
            </>
          ) : (
            <>
              <label>
                Espesor:
                <input
                  type={"text"}
                  name={"gauge"}
                  value={variation.gauge}
                  onChange={handleChange}
                />
              </label>
            </>
          )}
          {(product.department == "chain" ||
            product.department == "brazalete") &&
          isSolid ? (
            <>
              <label>
                Largo:
                <select
                  name="length"
                  onChange={handleChange}
                  onClick={handleChange}
                  value={variation.length}
                >
                  <option value="" selected disabled hidden>
                    Seleccione
                  </option>
                  {longitudes &&
                    longitudes.map((long) => {
                      return (
                        <option key={long} value={long}>
                          {long + " cm"}
                        </option>
                      );
                    })}
                </select>
              </label>
            </>
          ) : (
            <>
              <label>
                Medidas:
                <input
                  type={"text"}
                  name={"length"}
                  value={variation.length}
                  onChange={handleChange}
                />
              </label>
            </>
          )}
          <label>
            Peso:
            <input
              type={"text"}
              name={"weight"}
              value={variation.weight}
              onChange={handleChange}
            />
          </label>
          <label>
            Hechura:
            <WorkmanshipSelect
              costs={manufacturingCost.filter((cost) => cost.material === product.material)}
              value={variation.manufacturingCostId}
              onChange={(id) => setVariation((draft) => ({ ...draft, manufacturingCostId: id }))}
            />
          </label>
          {colorOptions.length > 0 && (
            <label>
              Color:
              <select
                name="color"
                onChange={handleChange}
                onClick={handleChange}
                value={variation.color}
              >
                <>
                  <option key={0} value="" selected disabled hidden>
                    Seleccione
                  </option>
                  <option value={ALL_COLORS}>Todos los colores</option>
                  {colorOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </>
              </select>
            </label>
          )}
          <>
            <label>
              Merma:
              <select
                name="wastage"
                onChange={handleChange}
                onClick={handleChange}
                value={variation.wastage}
              >
                <>
                  <option key={0} value="" selected disabled hidden>
                    Seleccione
                  </option>
                  {product.material === "gold" && (
                    <>
                      <option key={1} value={"810"}>
                        810
                      </option>
                      <option key={2} value={"825"}>
                        825
                      </option>
                    </>
                  )}
                  {product.material === "silver" && (
                    <option key={3} value={"NO_WASTAGE"}>
                      Sin merma
                    </option>
                  )}
                </>
              </select>
            </label>
          </>
        </div>
        {checkedVariation ? (
          <button type="submit">Agregar</button>
        ) : (
          <button type="submit" disabled={true}>
            Agregar
          </button>
        )}
      </form>
      {sortedVariations.length > 0 && (
        <div className="resultsAddVariationItems">
          {(product.department == "chain" ||
            product.department == "brazalete") &&
          isSolid ? (
            <Label
              tag="h3"
              style={{
                fontFamily: "Mont-Light",
                fontSize: "12px",
                textAlign: "start",
                width: "20%",
              }}
            >
              HILO
            </Label>
          ) : (
            <Label
              tag="h3"
              style={{
                fontFamily: "Mont-Light",
                fontSize: "12px",
                textAlign: "start",
                width: "20%",
              }}
            >
              ESPESOR
            </Label>
          )}
          {(product.department == "chain" ||
            product.department == "brazalete") &&
          isSolid ? (
            <Label
              tag="h3"
              style={{
                fontFamily: "Mont-Light",
                fontSize: "12px",
                textAlign: "start",
                width: "20%",
              }}
            >
              LARGO
            </Label>
          ) : (
            <Label
              tag="h3"
              style={{
                fontFamily: "Mont-Light",
                fontSize: "12px",
                textAlign: "start",
                width: "20%",
              }}
            >
              MEDIDAS
            </Label>
          )}
          <Label
            tag="h3"
            style={{
              fontFamily: "Mont-Light",
              fontSize: "12px",
              textAlign: "start",
              width: "20%",
            }}
          >
            PESO
          </Label>
          <Label
            tag="h3"
            style={{
              fontFamily: "Mont-Light",
              fontSize: "12px",
              textAlign: "start",
              width: "20%",
            }}
          >
            COLOR
          </Label>
          <Label
            tag="h3"
            style={{
              fontFamily: "Mont-Light",
              fontSize: "12px",
              textAlign: "start",
              width: "20%",
            }}
          >
            MERMA
          </Label>
          <Label
            tag="h3"
            style={{
              fontFamily: "Mont-Light",
              fontSize: "12px",
              textAlign: "start",
              width: "20%",
            }}
          >
            HECHURA
          </Label>

          <p
            style={{
              textAlign: "end",
              textDecoration: "underline",
              marginRight: 10,
              width: "20%",
            }}
          ></p>
        </div>
      )}
      {sortedVariations.map((vari) => {
        return (
          <div
            className="resultsAddVariationItems"
            key={vari.localId}
          >
            <p>{vari.gauge} </p>
            <p>
              {vari.length}{" "}
              {(product.department == "chain" ||
                product.department == "brazalete") &&
              isSolid
                ? "cm"
                : "mm"}
            </p>
            <p>{vari.weight} g</p>
            <p>{vari.color}</p>
            <p>{wastageLabel(vari.wastage)}</p>
            <p style={{ fontFamily: "Mont-bold" }}>
              {manufacturingCostVariation(vari)
                ? displayMoney(Number(manufacturingCostVariation(vari)))
                : " "}
            </p>

            <p
              onClick={() => removeVariation(vari)}
              style={{
                textAlign: "end",
                textDecoration: "underline",
                marginRight: 10,
                fontSize: isMobile ? "13px" : "14px",
                cursor: "pointer",
              }}
            >
              Borrar
            </p>
          </div>
        );
      })}
      {variations.length > 0 && (
        <div className="save_div">
          <button className="save" disabled={isSaving} onClick={() => saveVariations()}>
            Guardar cambios
          </button>
        </div>
      )}
    </div>
  );
};

export default AddVariation;
